import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  LinearProgress,
  IconButton,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { QRCodeSVG } from 'qrcode.react'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/shared/contexts/ToastContext'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { getLineStatus } from '@/features/internal/patient-messaging/apis/patientMessagingApi'
import { IntegrationToken } from '@/features/internal/patient-messaging/types/patientMessaging'
import { FONT_MONO } from '@/shared/styles/typography'
import { useCreateTokenMutation } from '@/features/internal/patient-messaging/hooks/mutations/useCreateTokenMutation'

interface LineLinkQRModalProps {
  open: boolean
  onClose: () => void
  tokenData: IntegrationToken
  patientId: string
}

const TOKEN_DURATION_SEC = 900 // 15分

function calcRemaining(expiresAt: string): number {
  const diff = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000)
  return Math.max(0, diff)
}

const LineLinkQRModal: React.FC<LineLinkQRModalProps> = ({
  open,
  onClose,
  tokenData: initialToken,
  patientId,
}) => {
  const [tokenData, setTokenData] = useState(initialToken)
  const [remainingSec, setRemainingSec] = useState(() =>
    calcRemaining(initialToken.expires_at)
  )
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const createTokenMutation = useCreateTokenMutation()

  // カウントダウンタイマー
  useEffect(() => {
    if (!open) return

    const timer = setInterval(() => {
      setRemainingSec(calcRemaining(tokenData.expires_at))
    }, 1000)

    return () => clearInterval(timer)
  }, [open, tokenData.expires_at])

  // 連携完了ポーリング（5秒間隔）
  useEffect(() => {
    if (!open || remainingSec <= 0) return

    const poller = setInterval(async () => {
      try {
        const lineStatus = await getLineStatus(patientId)
        if (lineStatus.conversation_thread_id) {
          clearInterval(poller)
          queryClient.invalidateQueries({
            queryKey: queryKeys.patientMessaging.lineStatus(patientId),
          })
          queryClient.invalidateQueries({
            queryKey: queryKeys.patientManagement.patients(),
          })
          showToast('LINE連携が完了しました', 'success')
          onClose()
        }
      } catch {
        // ポーリングエラーは無視
      }
    }, 5000)

    return () => clearInterval(poller)
  }, [open, remainingSec, patientId, queryClient, showToast, onClose])

  // トークン初期化
  useEffect(() => {
    setTokenData(initialToken)
    setRemainingSec(calcRemaining(initialToken.expires_at))
  }, [initialToken])

  const handleRegenerate = useCallback(async () => {
    try {
      const newToken = await createTokenMutation.mutateAsync(patientId)
      setTokenData(newToken)
      setRemainingSec(calcRemaining(newToken.expires_at))
    } catch {
      showToast('トークンの再生成に失敗しました', 'error')
    }
  }, [patientId, createTokenMutation, showToast])

  const handleCopyToken = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(tokenData.token)
      showToast('トークンをコピーしました', 'success')
    } catch {
      showToast('コピーに失敗しました', 'error')
    }
  }, [tokenData.token, showToast])

  const isExpired = remainingSec <= 0
  const progress = (remainingSec / TOKEN_DURATION_SEC) * 100
  const minutes = Math.floor(remainingSec / 60)
  const seconds = remainingSec % 60

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>LINE連携用QRコード</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 1,
          }}
        >
          {/* QRコード */}
          {!isExpired && tokenData.oamessage_url ? (
            <QRCodeSVG value={tokenData.oamessage_url} size={250} level="M" />
          ) : (
            <Box
              sx={{
                width: 250,
                height: 250,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F5F5F5',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {isExpired
                  ? 'QRコードの有効期限が切れました'
                  : 'QRコードを生成できません'}
              </Typography>
            </Box>
          )}

          {/* カウントダウン */}
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                残り時間
              </Typography>
              <Typography
                variant="caption"
                color={isExpired ? 'error' : 'text.secondary'}
              >
                {isExpired
                  ? '期限切れ'
                  : `${minutes}:${seconds.toString().padStart(2, '0')}`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ borderRadius: 1 }}
            />
          </Box>

          {/* トークンコピー */}
          {!isExpired && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                p: 1,
                backgroundColor: '#F5F5F5',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  fontFamily: FONT_MONO.fontFamily,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {tokenData.token}
              </Typography>
              <IconButton size="small" onClick={handleCopyToken}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* 期限切れ時の再生成 */}
          {isExpired && (
            <Button
              variant="outlined"
              onClick={handleRegenerate}
              disabled={createTokenMutation.isPending}
            >
              QRコードを再生成
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  )
}

export default LineLinkQRModal
